import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      if (user.role === "admin") {
        navigate("/admin");
      } else {
        navigate(location.state?.from || "/library/video");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="relative flex min-h-screen items-center justify-center px-6 grid-lines">
      <div className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 glow-blue" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md border border-white/10 bg-ink-800/70 p-10 backdrop-blur-xl"
      >
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Wall Board Academy</p>
          <h1 className="mt-3 font-head text-3xl font-black uppercase tracking-tight text-white">{t("login_here")}</h1>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("email")}</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-white/15 bg-ink-900/60 px-4 py-3 font-body text-white outline-none transition-colors focus:border-cyan"
            />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("password")}</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full border border-white/15 bg-ink-900/60 px-4 py-3 font-body text-white outline-none transition-colors focus:border-cyan"
            />
          </div>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="mt-2 bg-cyan px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors hover:bg-white disabled:opacity-50"
          >
            {loading ? "…" : t("sign_in")}
          </button>
        </form>
        <div className="mt-6 flex flex-col gap-2">
          <Link to="/forgot-password" data-testid="forgot-link" className="font-body text-sm text-slate-400 hover:text-cyan">{t("forgot_password")}</Link>
          <p className="font-body text-sm text-slate-500">
            {t("no_account")}{" "}
            <Link to="/register" data-testid="login-register-link" className="text-cyan hover:text-white">{t("request_here")}</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
